import React, { useState } from "react";
import {
  Card,
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { Tracker } from "../pages/Dashboard";
import { useI18n } from "../i18n";
import { formatDate, formatCurrency, formatTime } from "../utils/formatters";

// Import flag utility from AirportSelector
const getCountryFlag = (countryName: string): string => {
  const countryToCode: Record<string, string> = {
    Ireland: "IE",
    "United Kingdom": "GB",
    Italy: "IT",
    Spain: "ES",
    Portugal: "PT",
    Hungary: "HU",
    Poland: "PL",
    "Czech Republic": "CZ",
    Austria: "AT",
    Belgium: "BE",
    Netherlands: "NL",
    Greece: "GR",
    France: "FR",
    Germany: "DE",
    Croatia: "HR",
    Bulgaria: "BG",
    Sweden: "SE",
    Denmark: "DK",
    Norway: "NO",
    Finland: "FI",
    Switzerland: "CH",
    Morocco: "MA",
    Tunisia: "TN",
    Egypt: "EG",
    Jordan: "JO",
    Israel: "IL",
    Turkey: "TR",
    Malta: "MT",
    Cyprus: "CY",
    Albania: "AL",
    Serbia: "RS",
    "Bosnia & Herzegovina": "BA",
    Montenegro: "ME",
    "North Macedonia": "MK",
    Latvia: "LV",
    Lithuania: "LT",
    Estonia: "EE",
    Slovakia: "SK",
    Slovenia: "SI",
    Luxembourg: "LU",
  };

  const code = countryToCode[countryName];
  if (!code) return "🌍";

  // Convert country code to flag emoji
  const codePoints = code
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface TrackerCardProps {
  tracker: Tracker;
  onDelete: (id: number) => void;
  onShowChart: (tracker: Tracker) => void;
  onEditFlight: (trackerId: number, flightType: "outbound" | "return") => void;
  onThresholdUpdated: () => void;
  // Optional custom API functions for admin use
  customUpdateThreshold?: (trackerId: number, threshold: number | null) => Promise<void>;
  customDeleteTracker?: (trackerId: number) => Promise<void>;
}

const TrackerCard: React.FC<TrackerCardProps> = ({
  tracker,
  onDelete,
  onShowChart,
  onEditFlight,
  onThresholdUpdated,
  customUpdateThreshold,
  customDeleteTracker,
}) => {
  const currentPrice = formatCurrency(tracker.current_price);
  const flightDetails = tracker.flight_details;
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(
    tracker.threshold_price?.toString() || "",
  );
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { t } = useI18n();

  // Detect mobile viewport
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatAirport = (code: string, city?: string | null) => {
    return city ? `${city} (${code})` : code;
  };

  const normalizeDate = (d?: string | null) => (d ? d.toString().substring(0, 10) : null);

  const handleSaveThreshold = async () => {
    setSavingThreshold(true);
    try {
      const threshold = thresholdValue ? parseFloat(thresholdValue) : null;
      
      if (customUpdateThreshold) {
        await customUpdateThreshold(tracker.id, threshold);
      } else {
        await axios.put(`/api/trackers/${tracker.id}/threshold`, {
          threshold_price: threshold,
        });
      }
      
      toast.success(t("messages.thresholdUpdated") || "Threshold updated successfully!");
      setEditingThreshold(false);
      onThresholdUpdated();
    } catch (error) {
      console.error("Errore nell'aggiornamento della soglia:", error);
      toast.error(t("messages.thresholdUpdateError") || "Error updating threshold");
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleCancelThreshold = () => {
    setThresholdValue(tracker.threshold_price?.toString() || "");
    setEditingThreshold(false);
  };

  const handleBellClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion on mobile
    // Dispatch custom event to open the notification modal in NavigationBar
    window.dispatchEvent(new CustomEvent('openNotifications'));
  };

  return (
    <Card
      className={`h-100 price-card ${tracker.alert_sent ? "alert-indicator" : ""} ${isMobile && !isExpanded ? "mobile-collapsed" : ""}`}
      onClick={() => isMobile && !isExpanded && setIsExpanded(true)}
      style={isMobile && !isExpanded ? { cursor: 'pointer' } : {}}
    >
      <Card.Body>
        {isMobile && !isExpanded ? (
          // Mobile collapsed view
          <div className="mobile-collapsed-content">
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="fs-6">{getCountryFlag(tracker.origin_country || "")}</span>
                  <span className="fw-semibold small">{tracker.origin}</span>
                  <i className="bi bi-arrow-right small text-muted"></i>
                  <span className="fs-6">{getCountryFlag(tracker.destination_country || "")}</span>
                  <span className="fw-semibold small">{tracker.destination}</span>
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {tracker.return_date
                    ? `${formatDate(tracker.departure_date)} — ${formatDate(tracker.return_date)}`
                    : formatDate(tracker.departure_date)}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold text-primary">
                  {flightDetails
                    ? formatCurrency(flightDetails.total_price, flightDetails.total_original_price, flightDetails.original_currency)
                    : currentPrice}
                </span>
                {tracker.alert_sent && (
                  <i 
                    className="bi bi-bell-fill text-danger"
                    style={{ cursor: 'pointer', fontSize: '1.2rem' }}
                    onClick={handleBellClick}
                    title={t("notifications.viewNotifications")}
                  ></i>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Full expanded view (desktop or mobile expanded)
          <>
            {isMobile && isExpanded && (
              <div className="d-flex justify-content-end mb-2">
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 text-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(false);
                  }}
                >
                  <i className="bi bi-chevron-up"></i> {t("tracker.collapse")}
                </Button>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            {/* Two-line airport display with flags */}
            <div className="d-flex align-items-center mb-1">
              <span className="me-2 fs-5">{getCountryFlag(tracker.origin_country || "")}</span>
              <div className="flex-grow-1">
                <div className="fw-semibold text-primary">
                  {formatAirport(tracker.origin, tracker.origin_city)}
                </div>
              </div>
            </div>
            
            {/* Return flight or one-way indicator */}
            {tracker.return_date ? (
              <div className="d-flex align-items-center justify-content-center my-1">
                <div className="text-center">
                  <i className="bi bi-airplane-fill text-info me-1" title="Round trip"></i>
                  <i className="bi bi-airplane-fill text-info" style={{ display: 'inline-block', transform: 'rotate(180deg)' }} title="Round trip"></i>
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center my-1">
                <div className="text-center">
                  <i className="bi bi-airplane-fill text-secondary" style={{ display: 'inline-block', transform: 'rotate(180deg)' }} title="One way"></i>
                </div>
              </div>
            )}
            
            <div className="d-flex align-items-center">
              <span className="me-2 fs-5">{getCountryFlag(tracker.destination_country || "")}</span>
              <div className="flex-grow-1">
                <div className="fw-semibold text-success">
                  {formatAirport(tracker.destination, tracker.destination_city)}
                </div>
              </div>
            </div>
            
            <div className="text-muted small mt-1">
              {tracker.return_date
                ? `${formatDate(tracker.departure_date)} — ${formatDate(tracker.return_date)}`
                : formatDate(tracker.departure_date)}
              {tracker.original_departure_date &&
                normalizeDate(tracker.departure_date) !==
                  normalizeDate(tracker.original_departure_date) && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {t("tracker.originalDeparture")}:{" "}
                        {formatDate(tracker.original_departure_date)}
                      </Tooltip>
                    }
                  >
                    <span className="badge bg-info text-dark ms-2">
                      {t("tracker.modified")}
                    </span>
                  </OverlayTrigger>
                )}
              {tracker.return_date &&
                tracker.original_return_date &&
                normalizeDate(tracker.return_date) !== normalizeDate(tracker.original_return_date) && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {t("tracker.originalReturn")}:{" "}
                        {formatDate(tracker.original_return_date)}
                      </Tooltip>
                    }
                  >
                    <span className="badge bg-info text-dark ms-2">
                      {t("tracker.modified")}
                    </span>
                  </OverlayTrigger>
                )}
            </div>
          </div>
          {tracker.alert_sent && (
            <i 
              className="bi bi-bell-fill text-danger"
              style={{ cursor: 'pointer', fontSize: '1.5rem' }}
              onClick={handleBellClick}
              title={t("notifications.viewNotifications")}
            ></i>
          )}
        </div>

        {/* Flight Details */}
        {flightDetails && (
          <>
            <hr />
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-start">
                <strong>
                  <i className="bi bi-airplane-fill text-primary"></i> {t("tracker.outbound")}
                </strong>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-primary"
                  onClick={() => onEditFlight(tracker.id, "outbound")}
                  title={t("tracker.editOutbound")}
                >
                  <i className="bi bi-pencil-square"></i>
                </Button>
              </div>
              <div className="ms-3 mt-1">
                <small className="d-block">
                  <strong>{t("tracker.flightLabel")}</strong>: {flightDetails.outbound.flight_number}
                </small>
                <small className="d-block">
                  <strong>{t("tracker.timeLabel")}</strong>: {formatTime(flightDetails.outbound.departure_time)} - {formatTime(flightDetails.outbound.arrival_time)}
                </small>
                <small className="d-block">
                  <strong>{t("tracker.priceLabel")}</strong>: {formatCurrency(flightDetails.outbound.price, flightDetails.outbound.original_price, flightDetails.outbound.original_currency)}
                </small>
              </div>
            </div>
            {flightDetails.return && (
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <strong>
                    <i className="bi bi-airplane text-success"></i> {t("tracker.return")}
                  </strong>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary"
                    onClick={() => onEditFlight(tracker.id, "return")}
                    title={t("tracker.editReturn")}
                  >
                    <i className="bi bi-pencil-square"></i>
                  </Button>
                </div>
                <div className="ms-3 mt-1">
                  <small className="d-block">
                    <strong>{t("tracker.flightLabel")}</strong>: {flightDetails.return.flight_number}
                  </small>
                  <small className="d-block">
                    <strong>{t("tracker.timeLabel")}</strong>: {formatTime(flightDetails.return.departure_time)} - {formatTime(flightDetails.return.arrival_time)}
                  </small>
                  <small className="d-block">
                    <strong>{t("tracker.priceLabel")}</strong>: {formatCurrency(flightDetails.return.price, flightDetails.return.original_price, flightDetails.return.original_currency)}
                  </small>
                </div>
              </div>
            )}
          </>
        )}

        <hr />

        <div className="d-flex justify-content-between align-items-center mb-2">
          <span>
            <strong>{t("tracker.totalPrice")}</strong>
          </span>
          <span className="fs-4 text-primary">
            {flightDetails
              ? formatCurrency(flightDetails.total_price, flightDetails.total_original_price, flightDetails.original_currency)
              : currentPrice}
          </span>
        </div>

        {/* Threshold Price Section */}
        <div className="mb-3">
          {editingThreshold ? (
            <div>
              <label className="form-label small">
                {t("tracker.thresholdLabel")}
              </label>
              <InputGroup size="sm">
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t("addTracker.thresholdPlaceholder")}
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(e.target.value)}
                  disabled={savingThreshold}
                />
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSaveThreshold}
                  disabled={savingThreshold}
                >
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelThreshold}
                  disabled={savingThreshold}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </InputGroup>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {tracker.threshold_price
                  ? `${t("tracker.thresholdPrefix")}${formatCurrency(tracker.threshold_price)}`
                  : t("tracker.noThreshold")}
              </small>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-primary"
                onClick={() => setEditingThreshold(true)}
                title={tracker.threshold_price ? t("tracker.edit") : t("tracker.set")}
              >
                <i className="bi bi-pencil-square"></i>
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onShowChart(tracker)}
          >
            <i className="bi bi-graph-up"></i> {t("tracker.graph")}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="float-end"
            onClick={() => onDelete(tracker.id)}
          >
            <i className="bi bi-trash"></i> {t("tracker.delete")}
          </Button>
        </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default React.memo(TrackerCard);
